// src/App.jsx
import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, CircularProgress, Box } from '@mui/material';

// Import i18n configuration
import './i18n';

import { AuthProvider } from './context/AuthContext';
import { InteractionProvider } from './context/InteractionContext';
import { NotificationsProvider } from './context/NotificationsContext';
import { ColorModeContext, useMode } from "./theme";
import { NavigationStackProvider } from './context/NavigationStackContext';
import { BreadcrumbProvider } from './context/BreadcrumbContext';

// Components
import AuthGuard from './features/auth/components/AuthGuard';
import SessionTimeout from './features/auth/components/SessionTimeout';
import ForbiddenErrorDialog from './features/auth/components/ForbiddenErrorDialog';
import MainLayout from './components/layout/Layout/MainLayout';
import { ForbiddenErrorProvider } from './features/auth/context/ForbiddenErrorContext';

// Feature Routes
import { AuthRoutes } from './features/auth/Routes';
import { DashboardRoutes } from './features/dashboard/Routes';
import { HomeRoutes } from './features/home/Routes';
import { TaskRoutes } from './features/tasks/Routes';
import { StaffRoutes } from './features/staff/Routes';
import { RoleRoutes } from './features/roles/Routes';
import { NotificationRoutes } from './features/notifications/Routes';
import { DepartmentRoutes } from './features/departments/Routes';
import { ErrorManagementRoutes } from './features/error-management/Routes';
import { FeedbackRoutes } from './features/feedback/Routes';
import { ChangelogRoutes } from './features/changelog/Routes';
import { SoftwareLicenseRoutes } from './features/softwareLicenses/Routes';
import { FileRoutes } from './features/files/Routes';
import { useGlobalDropGuard } from './features/files/upload';

import ThemeColorShowcase from './components/common/ThemeShowcase';
import I18nLoader from './i18n/I18nLoader';
import { SnackbarProvider } from './context/SnackbarContext';
import { PageInstanceProvider } from './context/PageInstanceContext';
import { ProtectedAuthRoutes } from './features/auth/ProtectedRoutes';
import NullLayout from './components/layout/Layout/NullLayout';
import { RoomsRoutes } from './features/rooms/Routes';
import { EquipmentRoutes } from './features/equipment/Routes';
import { SetupRoutes } from './features/setup/Routes';
import { InfoscreensRoutes } from './features/infoscreens/Routes';
import { ProtectedInfoscreensRoutes } from './features/infoscreens/ProtectedRoutes';
import { ProceduresRoutes } from './features/procedures/Routes';
import { SettingsRoutes } from './features/settings/Routes';
import { InventoryRoutes, InventoryScannerRoutes, InventoryScannerDeviceRoutes } from './features/inventory/Routes';
import { DelegationRoutes } from './features/delegations/Routes';
import { ReportingRoutes } from './features/reporting/Routes';
import { AdminTemplatesRoutes } from './features/admin-templates/Routes';
import { ContactsRoutes } from './features/contacts/Routes';
import { OnboardingRoutes } from './features/onboarding/Routes';
import { TidsregistreringRoutes } from './features/tidsregistering/Routes';

function App() {
  const [theme, colorMode] = useMode();
  // A file dropped outside an upload area would otherwise open in place of the app
  useGlobalDropGuard();

  return (
    <ThemeProvider theme={theme}>
      <ColorModeContext.Provider value={colorMode}>
        <CssBaseline />
        <SnackbarProvider>
        <I18nLoader>
          <InteractionProvider>
          <PageInstanceProvider>
            <NavigationStackProvider>
            <BreadcrumbProvider>
              <AuthProvider>
                <ForbiddenErrorProvider>
                <NotificationsProvider>
                  <SessionTimeout />
                  <ForbiddenErrorDialog />
                  <Routes>
                    {/* Public routes */}
                    {AuthRoutes()}
                    {ProtectedInfoscreensRoutes()}
                    {InventoryScannerDeviceRoutes()}

                    <Route path="/colors" element={<ThemeColorShowcase />} />
                    <Route path="/" element={<AuthGuard><NullLayout /></AuthGuard>}>
                      {ProtectedAuthRoutes()}
                      {InventoryScannerRoutes()}
                    </Route>

                    {/* Protected routes */}
                    <Route path="/" element={<AuthGuard><MainLayout /></AuthGuard>}>
                      <Route index element={<Navigate to="/home" replace />} />

                      {/* Feature routes */}
                      {HomeRoutes()}
                      {DashboardRoutes()}
                      {ChangelogRoutes()}
                      {FeedbackRoutes()}
                      {TaskRoutes()}
                      {StaffRoutes()}
                      {RoleRoutes()}
                      {NotificationRoutes()}
                      {DepartmentRoutes()}
                      {ErrorManagementRoutes()}
                      {SoftwareLicenseRoutes()}
                      {FileRoutes()}
                      {RoomsRoutes()}
                      {EquipmentRoutes()}
                      {SetupRoutes()}
                      {InfoscreensRoutes()}
                      {ProceduresRoutes()}
                      {SettingsRoutes()}
                      {InventoryRoutes()}
                      {DelegationRoutes()}
                      {ReportingRoutes()}
                      {AdminTemplatesRoutes()}
                      {ContactsRoutes()}
                      {OnboardingRoutes()}
                      {TidsregistreringRoutes()}
                    </Route>

                    {/* Catch all route */}
                    <Route path="*" element={<Navigate to="/home" replace />} />
                  </Routes>
                </NotificationsProvider>
                </ForbiddenErrorProvider>
              </AuthProvider>
            </BreadcrumbProvider>
            </NavigationStackProvider>
          </PageInstanceProvider>
          </InteractionProvider>
        </I18nLoader>
        </SnackbarProvider>
      </ColorModeContext.Provider>
    </ThemeProvider>
  );
}

export default App;